import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Route, Zap, Package, MapPin, Clock, DollarSign, TrendingUp,
  RefreshCw, Play, Pause, CheckCircle2, AlertTriangle, Box,
  Maximize2, Minimize2, Navigation, Layers
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';

const RouteOptimization = () => {
  const [activeView, setActiveView] = useState('consolidation'); // consolidation, routing, cubing
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);

  // Mock data for load consolidation
  const pendingRequests = [
    {
      id: 'LOG-2024-001',
      from: { name: 'Main Warehouse', location: 'Riyadh', lat: 24.7136, lng: 46.6753 },
      to: { name: 'Store #45', location: 'Jeddah', lat: 21.4858, lng: 39.1925 },
      items: 25,
      weight: 14500,
      volume: 12.5,
      priority: 'high',
      requiredDate: '2024-01-29'
    },
    {
      id: 'LOG-2024-005',
      from: { name: 'Main Warehouse', location: 'Riyadh', lat: 24.7136, lng: 46.6753 },
      to: { name: 'Store #12', location: 'Dammam', lat: 26.4207, lng: 50.0888 },
      items: 15,
      weight: 45000,
      volume: 20.0,
      priority: 'high',
      requiredDate: '2024-01-30'
    },
    {
      id: 'LOG-2024-006',
      from: { name: 'Distribution Center A', location: 'Jeddah', lat: 21.4858, lng: 39.1925 },
      to: { name: 'Client ABC', location: 'Riyadh', lat: 24.7136, lng: 46.6753 },
      items: 50,
      weight: 12500,
      volume: 15.0,
      priority: 'medium',
      requiredDate: '2024-01-30'
    }
  ];

  // Mock optimized routes
  const optimizedRoutes = [
    {
      id: 'ROUTE-001',
      vehicle: 'VH-001',
      driver: 'Ahmed Al-Rashid',
      requests: ['LOG-2024-001', 'LOG-2024-006'],
      totalWeight: 27000,
      totalVolume: 27.5,
      distance: 950,
      estimatedTime: '8h 30m',
      estimatedCost: 2800,
      fuelSavings: 450,
      efficiency: 96.2,
      waypoints: [
        { name: 'Main Warehouse', location: 'Riyadh', type: 'pickup', order: 1 },
        { name: 'Distribution Center A', location: 'Jeddah', type: 'pickup', order: 2 },
        { name: 'Store #45', location: 'Jeddah', type: 'delivery', order: 3 },
        { name: 'Client ABC', location: 'Riyadh', type: 'delivery', order: 4 }
      ],
      status: 'optimized'
    },
    {
      id: 'ROUTE-002',
      vehicle: 'VH-004',
      driver: 'Mohammed Ali',
      requests: ['LOG-2024-005'],
      totalWeight: 45000,
      totalVolume: 20.0,
      distance: 420,
      estimatedTime: '4h 15m',
      estimatedCost: 1800,
      fuelSavings: 0,
      efficiency: 94.8,
      waypoints: [
        { name: 'Main Warehouse', location: 'Riyadh', type: 'pickup', order: 1 },
        { name: 'Store #12', location: 'Dammam', type: 'delivery', order: 2 }
      ],
      status: 'optimized'
    }
  ];

  // Mock cubing visualization data
  const cubingData = {
    vehicle: 'VH-001',
    capacity: { weight: 15000, volume: 45.0, length: 12, width: 2.5, height: 2.4 },
    items: [
      { id: 'ITEM-001', name: 'Laptop Dell XPS', quantity: 25, weight: 500, volume: 0.5, length: 0.4, width: 0.3, height: 0.05 },
      { id: 'ITEM-002', name: 'Printer HP', quantity: 10, weight: 200, volume: 0.2, length: 0.5, width: 0.4, height: 0.1 },
      { id: 'ITEM-003', name: 'Office Chair', quantity: 50, weight: 2500, volume: 15.0, length: 0.6, width: 0.6, height: 1.2 }
    ],
    utilization: { weight: 87.5, volume: 88.9 },
    suggestedLayout: [
      { item: 'ITEM-003', position: { x: 0, y: 0, z: 0 }, orientation: 'vertical', count: 50 },
      { item: 'ITEM-001', position: { x: 0, y: 0, z: 1.2 }, orientation: 'horizontal', count: 25 },
      { item: 'ITEM-002', position: { x: 0, y: 0, z: 1.7 }, orientation: 'horizontal', count: 10 }
    ]
  };

  // Mock routing performance data
  const routingPerformance = [
    { route: 'Route 1', distance: 950, time: 510, cost: 2800, efficiency: 96.2 },
    { route: 'Route 2', distance: 420, time: 255, cost: 1800, efficiency: 94.8 },
    { route: 'Route 3', distance: 680, time: 405, cost: 2200, efficiency: 92.1 },
    { route: 'Route 4', distance: 1100, time: 620, cost: 3200, efficiency: 95.5 }
  ];

  const handleOptimize = () => {
    setIsOptimizing(true);
    setTimeout(() => {
      setIsOptimizing(false);
      alert('Route optimization completed! Consolidated 3 requests into 2 optimized routes, saving 450 SAR in fuel costs.');
    }, 2000);
  };

  const renderConsolidation = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Load Consolidation</h3>
            <p className="text-sm text-gray-600 mt-1">Automatically group multiple requests into optimized routes</p>
          </div>
          <button
            onClick={handleOptimize}
            disabled={isOptimizing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isOptimizing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Optimizing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Optimize Routes
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Pending Requests</p>
            <p className="text-2xl font-bold text-gray-900">{pendingRequests.length}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Potential Savings</p>
            <p className="text-2xl font-bold text-green-600">SAR 450</p>
          </div>
        </div>

        <div className="space-y-3">
          {pendingRequests.map((request) => (
            <div key={request.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{request.id}</p>
                    <p className="text-sm text-gray-600">
                      {request.from.name} → {request.to.name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Weight</p>
                    <p className="font-medium text-gray-900">{request.weight.toLocaleString()} kg</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Volume</p>
                    <p className="font-medium text-gray-900">{request.volume} m³</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    request.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {request.priority}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Optimized Routes */}
      {optimizedRoutes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Optimized Routes</h3>
          <div className="space-y-4">
            {optimizedRoutes.map((route) => (
              <motion.div
                key={route.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 border-2 border-green-200 bg-green-50 rounded-lg"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">{route.id}</h4>
                    <p className="text-sm text-gray-600">Vehicle: {route.vehicle} • Driver: {route.driver}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Efficiency</p>
                    <p className="text-lg font-bold text-green-600">{route.efficiency}%</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Distance</p>
                    <p className="font-medium text-gray-900">{route.distance} km</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Estimated Time</p>
                    <p className="font-medium text-gray-900">{route.estimatedTime}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Cost</p>
                    <p className="font-medium text-gray-900">SAR {route.estimatedCost.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Fuel Savings</p>
                    <p className="font-medium text-green-600">SAR {route.fuelSavings}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-900 mb-2">Route Waypoints</p>
                  <div className="space-y-2">
                    {route.waypoints.map((waypoint, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 bg-white rounded-lg">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          waypoint.type === 'pickup' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                        }`}>
                          {waypoint.order}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{waypoint.name}</p>
                          <p className="text-xs text-gray-600">{waypoint.location}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          waypoint.type === 'pickup' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {waypoint.type}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => alert(`Assigning route ${route.id} to vehicle ${route.vehicle}`)}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Assign Route
                  </button>
                  <button
                    onClick={() => setSelectedRoute(route.id === selectedRoute ? null : route.id)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderRouting = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Dynamic Routing Performance</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={routingPerformance}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="route" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="distance" fill="#3b82f6" name="Distance (km)" />
            <Line yAxisId="right" type="monotone" dataKey="efficiency" stroke="#10b981" strokeWidth={2} name="Efficiency (%)" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Route Optimization</h3>
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <Zap className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-gray-900">Traffic-Aware Routing</span>
            </div>
            <p className="text-sm text-gray-600">
              Routes are optimized based on real-time traffic data, road restrictions, and vehicle-specific limitations.
            </p>
          </div>
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="font-medium text-gray-900">Fuel Cost Optimization</span>
            </div>
            <p className="text-sm text-gray-600">
              AI calculates the most fuel-efficient paths, considering distance, traffic, and vehicle fuel consumption rates.
            </p>
          </div>
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <Navigation className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-900">Road Restriction Compliance</span>
            </div>
            <p className="text-sm text-gray-600">
              Routes automatically avoid roads with weight/height restrictions based on vehicle specifications.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCubing = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cubing & Space Allocation</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Vehicle: {cubingData.vehicle}</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Capacity</p>
                  <p className="font-medium text-gray-900">{cubingData.capacity.weight.toLocaleString()} kg</p>
                  <p className="text-xs text-gray-600">{cubingData.capacity.volume} m³</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Dimensions</p>
                  <p className="font-medium text-gray-900">{cubingData.capacity.length}m × {cubingData.capacity.width}m</p>
                  <p className="text-xs text-gray-600">Height: {cubingData.capacity.height}m</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Utilization</p>
                  <p className="font-medium text-gray-900">{cubingData.utilization.weight}%</p>
                  <p className="text-xs text-gray-600">Volume: {cubingData.utilization.volume}%</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900">Items to Load</h4>
              {cubingData.items.map((item) => (
                <div key={item.id} className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <span className="text-sm text-gray-600">Qty: {item.quantity}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-gray-600">Weight</p>
                      <p className="font-medium">{item.weight} kg</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Volume</p>
                      <p className="font-medium">{item.volume} m³</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Dimensions</p>
                      <p className="font-medium">{item.length}m × {item.width}m × {item.height}m</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Suggested Loading Layout</h4>
            <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="relative" style={{ height: '400px', width: '100%' }}>
                {/* Visual representation of loading layout */}
                <div className="absolute inset-0 border-2 border-blue-500 rounded" style={{ 
                  width: '100%', 
                  height: '100%',
                  background: 'linear-gradient(to bottom, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))'
                }}>
                  {cubingData.suggestedLayout.map((layout, index) => (
                    <div
                      key={index}
                      className="absolute bg-blue-500 border-2 border-blue-700 rounded"
                      style={{
                        left: `${layout.position.x * 8}%`,
                        bottom: `${layout.position.z * 10}%`,
                        width: `${layout.item === 'ITEM-003' ? 30 : 20}%`,
                        height: `${layout.item === 'ITEM-003' ? 40 : 10}%`,
                        opacity: 0.7
                      }}
                    >
                      <div className="text-white text-xs p-1 font-medium">{layout.item}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {cubingData.suggestedLayout.map((layout, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-white rounded">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{layout.item}</p>
                      <p className="text-xs text-gray-600">
                        Position: ({layout.position.x}, {layout.position.y}, {layout.position.z}) • 
                        Orientation: {layout.orientation}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{layout.count} units</span>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => alert('Loading layout optimized! This would generate detailed loading instructions for the driver.')}
              className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Generate Loading Instructions
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Load & Route Optimization</h2>
            <p className="text-sm text-gray-600 mt-1">Consolidate loads, optimize routes, and maximize space utilization</p>
          </div>
        </div>

        {/* View Tabs */}
        <div className="mt-4 flex gap-2">
          {[
            { id: 'consolidation', label: 'Load Consolidation', icon: Package },
            { id: 'routing', label: 'Dynamic Routing', icon: Route },
            { id: 'cubing', label: 'Cubing & Space', icon: Box }
          ].map((view) => {
            const Icon = view.icon;
            return (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeView === view.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {view.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {activeView === 'consolidation' && renderConsolidation()}
      {activeView === 'routing' && renderRouting()}
      {activeView === 'cubing' && renderCubing()}
    </div>
  );
};

export default RouteOptimization;
